import React, { useRef } from "react";
import { FaDownload } from "react-icons/fa";
import html2canvas from "html2canvas";

const ContactCard = ({ contact, onEdit, onDelete, canDelete = true }) => {
  const buttonRef = useRef(null);
  const cardRef = useRef(null);

  if (!contact) return null;

  const {
    name,
    phone_number,
    email,
    job_title,
    address,
    city,
    country,
    zipcode,
  } = contact;

  const handleDownload = async () => {
    if (!cardRef.current) return;

    try {
      // Hide all action buttons temporarily for the screenshot
      const actionButtons = cardRef.current.querySelector(".action-buttons");
      const originalDisplay = actionButtons ? actionButtons.style.display : "";
      
      if (actionButtons) {
        actionButtons.style.visibility = "hidden";
        actionButtons.style.pointerEvents = "none";
      }

      // Wait a brief moment for the DOM to update
      await new Promise((resolve) => setTimeout(resolve, 150));

      // Capture the card as canvas with better settings
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: "#ffffff",
        scale: 3,
        useCORS: true,
        allowTaint: false,
        logging: false,
        windowWidth: cardRef.current.scrollWidth,
        windowHeight: cardRef.current.scrollHeight,
        x: 0,
        y: 0,
      });

      // Show the action buttons again
      if (actionButtons) {
        actionButtons.style.visibility = "visible";
        actionButtons.style.pointerEvents = "auto";
      }

      // Convert canvas to blob and download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.download = `contact-card-${name
          .replace(/\s+/g, "-")
          .toLowerCase()}.png`;
        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, "image/png");
    } catch (error) {
      console.error("Error downloading contact card:", error);
    }
  };

  // Generate initials for placeholder
  const getInitials = (name) => {
    return name
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div
      ref={cardRef}
      className="bg-white border rounded-xl p-4 shadow-sm relative"
    >
      {/* Top Section with Profile and Name */}
      <div className="flex items-center mb-4">
        {/* Profile Image Placeholder */}
        <div className="w-10 h-10  bg-gray-200 rounded-lg flex items-center justify-center mr-3">
          <span className="text-gray-600 font-semibold text-lg">
            {getInitials(name)}
          </span>
        </div>

        {/* Name */}
        <div className="flex-1">
          <h3 className="text-lg font-bold text-gray-900">{name}</h3>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons flex items-center gap-2">
          <button
            onClick={() => onEdit(contact)}
            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Edit contact"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          
          {canDelete && (
            <button
              onClick={() => onDelete(contact)}
              className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Delete contact"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
          
          <button
            ref={buttonRef}
            onClick={handleDownload}
            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Download contact card as PNG"
          >
            <FaDownload className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Contact Details Section */}
      <div className="bg-[#F7F7F7] rounded-lg p-3 min-h-24">
        <div className="space-y-2">
          {phone_number && (
            <div className="flex items-center gap-3">
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.5257 2.5C8.01407 2.59528 8.46289 2.83413 8.81473 3.18597C9.16657 3.53781 9.40542 3.98663 9.5007 4.475M7.5257 0.5C8.54034 0.612718 9.48649 1.06709 10.2088 1.7885C10.9311 2.50992 11.3867 3.4555 11.5007 4.47M11.0007 8.46V9.96C11.0013 10.0993 10.9727 10.2371 10.917 10.3647C10.8612 10.4923 10.7793 10.6068 10.6767 10.7009C10.5741 10.7951 10.453 10.8667 10.3211 10.9114C10.1892 10.956 10.0494 10.9725 9.9107 10.96C8.37212 10.7928 6.8942 10.2671 5.5957 9.425C4.38761 8.65733 3.36337 7.63309 2.5957 6.425C1.75069 5.1206 1.22482 3.6355 1.0607 2.09C1.0482 1.95173 1.06464 1.81238 1.10895 1.68081C1.15326 1.54924 1.22448 1.42834 1.31808 1.32581C1.41168 1.22328 1.5256 1.14135 1.65259 1.08526C1.77959 1.02917 1.91687 1.00013 2.0557 1H3.5557C3.79835 0.997612 4.03359 1.08354 4.21758 1.24177C4.40156 1.39999 4.52174 1.61972 4.5557 1.86C4.61901 2.34003 4.73642 2.81136 4.9057 3.265C4.97297 3.44396 4.98753 3.63846 4.94765 3.82544C4.90777 4.01242 4.81513 4.18406 4.6807 4.32L4.0457 4.955C4.75748 6.20677 5.79393 7.24322 7.0457 7.955L7.6807 7.32C7.81664 7.18557 7.98828 7.09292 8.17526 7.05305C8.36224 7.01317 8.55674 7.02773 8.7357 7.095C9.18934 7.26428 9.66067 7.38169 10.1407 7.445C10.3836 7.47926 10.6054 7.6016 10.764 7.78875C10.9225 7.97589 11.0068 8.21479 11.0007 8.46Z"
                  stroke="#1E1E1E"
                  stroke-width="1.2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span className="text-sm text-[#222A35]">{phone_number}</span>
            </div>
          )}

          {email && (
            <div className="flex items-center gap-3">
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11 3C11 2.45 10.55 2 10 2H2C1.45 2 1 2.45 1 3M11 3V9C11 9.55 10.55 10 10 10H2C1.45 10 1 9.55 1 9V3M11 3L6 6.5L1 3"
                  stroke="#1E1E1E"
                  stroke-width="1.2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>

              <span className="text-sm text-[#222A35] break-all">{email}</span>
            </div>
          )}
          {address && (
            <div className="flex items-center gap-3">
              <svg
                width="13"
                height="12"
                viewBox="0 0 13 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clip-path="url(#clip0_774_16081)">
                  <path
                    d="M11.166 5C11.166 8.5 6.66602 11.5 6.66602 11.5C6.66602 11.5 2.16602 8.5 2.16602 5C2.16602 3.80653 2.64012 2.66193 3.48404 1.81802C4.32795 0.974106 5.47254 0.5 6.66602 0.5C7.85949 0.5 9.00408 0.974106 9.848 1.81802C10.6919 2.66193 11.166 3.80653 11.166 5Z"
                    stroke="#1E1E1E"
                    stroke-width="1.2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M6.66602 6.5C7.49444 6.5 8.16602 5.82843 8.16602 5C8.16602 4.17157 7.49444 3.5 6.66602 3.5C5.83759 3.5 5.16602 4.17157 5.16602 5C5.16602 5.82843 5.83759 6.5 6.66602 6.5Z"
                    stroke="#1E1E1E"
                    stroke-width="1.2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_774_16081">
                    <rect
                      width="12"
                      height="12"
                      fill="white"
                      transform="translate(0.666016)"
                    />
                  </clipPath>
                </defs>
              </svg>

              <span className="text-sm text-[#222A35] break-all">
                {address} , {city} , {country} , {zipcode}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactCard;
